import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Zap, Shield, TrendingUp, Download, Upload, Activity, CheckCircle2, ArrowRight, Gauge } from 'lucide-react';
import axios from 'axios';

// Modern Digital Meter Component
const DigitalMeter = ({ value, label, color, unit, icon: Icon }) => {
  return (
    <div className="relative">
      <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 border-4 ${
        color === 'cyan' ? 'border-cyan-500' : color === 'purple' ? 'border-purple-500' : 'border-blue-500'
      } shadow-2xl`}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-full ${
            color === 'cyan' ? 'bg-cyan-500' : color === 'purple' ? 'bg-purple-500' : 'bg-blue-500'
          } flex items-center justify-center shadow-lg`}>
            <Icon className="h-8 w-8 text-white" />
          </div>
        </div>
        
        {/* Label */}
        <div className="text-center mb-3">
          <p className="text-cyan-200 text-sm font-semibold uppercase tracking-wider">{label}</p>
        </div>
        
        {/* Digital Display */}
        <div className="relative bg-black/50 rounded-xl p-6 mb-4">
          <div className="flex items-end justify-center gap-2">
            <span className={`font-mono text-6xl font-bold ${
              color === 'cyan' ? 'text-cyan-400' : color === 'purple' ? 'text-purple-400' : 'text-blue-400'
            } tracking-tight`}>
              {value.toFixed(2)}
            </span>
            <span className="text-cyan-300 text-2xl font-bold pb-2">{unit}</span>
          </div>
          
          {/* Animated bars */}
          <div className="mt-4 flex gap-1 justify-center">
            {[...Array(20)].map((_, i) => {
              const threshold = (i / 20) * 200;
              const isActive = value > threshold;
              return (
                <div
                  key={i}
                  className={`w-2 h-8 rounded-full transition-all duration-300 ${
                    isActive 
                      ? color === 'cyan' 
                        ? 'bg-cyan-500' 
                        : color === 'purple' 
                        ? 'bg-purple-500' 
                        : 'bg-blue-500'
                      : 'bg-slate-700'
                  }`}
                  style={{ 
                    height: `${(i + 1) * 4}px`,
                    opacity: isActive ? 1 : 0.3
                  }}
                />
              );
            })}
          </div>
        </div>
        
        {/* Status Indicator */}
        <div className="flex items-center justify-center gap-2">
          <div className={`w-3 h-3 rounded-full ${
            value > 0 
              ? color === 'cyan' 
                ? 'bg-cyan-500 animate-pulse' 
                : color === 'purple' 
                ? 'bg-purple-500 animate-pulse' 
                : 'bg-blue-500 animate-pulse'
              : 'bg-slate-600'
          }`}></div>
          <span className="text-cyan-300 text-sm">
            {value > 0 ? 'Active' : 'Standby'}
          </span>
        </div>
      </div>
    </div>
  );
};

const SpeedTest = () => {
  const navigate = useNavigate();
  const [testing, setTesting] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [currentDownload, setCurrentDownload] = useState(0);
  const [currentUpload, setCurrentUpload] = useState(0);
  const [currentPing, setCurrentPing] = useState(0);
  const [testPhase, setTestPhase] = useState('');
  const [results, setResults] = useState({
    download: 0,
    upload: 0,
    ping: 0,
    server: ''
  });

  const getSpeedVerdict = (download) => {
    if (download > 50) return { text: "Your connection is lightning fast ⚡ — perfect for streaming and gaming.", color: "text-green-600" };
    if (download > 20) return { text: "Good connection! Suitable for most online activities.", color: "text-yellow-600" };
    return { text: "Hmm… looks like your network could use a boost.", color: "text-red-600" };
  };

  const startSpeedTest = async () => {
    setTesting(true);
    setTestComplete(false);
    setCurrentDownload(0);
    setCurrentUpload(0);
    setCurrentPing(0);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Call backend speed test endpoint
      setTestPhase('testing');
      const response = await axios.get(`${backendUrl}/api/speed-test`);
      
      const finalResults = response.data;
      
      // Animate the results for visual effect
      // Animate ping
      setTestPhase('ping');
      for (let i = 0; i <= 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 50));
        setCurrentPing((finalResults.ping / 10) * i);
      }
      
      // Animate download
      setTestPhase('download');
      for (let i = 0; i <= 20; i++) {
        await new Promise(resolve => setTimeout(resolve, 50));
        setCurrentDownload((finalResults.download / 20) * i);
      }
      
      // Animate upload
      setTestPhase('upload');
      for (let i = 0; i <= 20; i++) {
        await new Promise(resolve => setTimeout(resolve, 50));
        setCurrentUpload((finalResults.upload / 20) * i);
      }
      
      // Set final results
      setResults(finalResults);
      setCurrentDownload(finalResults.download);
      setCurrentUpload(finalResults.upload);
      setCurrentPing(finalResults.ping);
      
      setTesting(false);
      setTestComplete(true);
    } catch (error) {
      console.error('Speed test error:', error);
      setTesting(false);
    }
  };

  const features = [
    {
      icon: <Zap className="h-10 w-10" />,
      title: "Real-Time Precision",
      description: "Server-side testing for accurate results"
    },
    {
      icon: <Shield className="h-10 w-10" />,
      title: "No Ads, No Hassle",
      description: "Just clean, fast testing"
    },
    {
      icon: <TrendingUp className="h-10 w-10" />,
      title: "Smart Insights",
      description: "Understand how your speed affects streaming, gaming, and downloads"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-violet-900">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Test Your Internet Speed in Seconds
          </h1>
          <p className="text-xl md:text-2xl text-cyan-200 mb-12 max-w-3xl mx-auto">
            Real server-side speed testing with accurate download, upload, and ping measurements.
          </p>

          {/* Speed Test Container */}
          <div className="max-w-5xl mx-auto">
            {!testing && !testComplete && (
              <div className="mb-8">
                <Button
                  onClick={startSpeedTest}
                  className="h-20 px-12 text-2xl font-bold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-4 border-cyan-300 shadow-2xl hover:scale-105 transition-transform"
                >
                  <Gauge className="h-8 w-8 mr-3" />
                  Start Speed Test
                </Button>
                <p className="text-cyan-200 mt-6 text-lg">
                  No sign-up required. Instant, accurate, and free.
                </p>
              </div>
            )}

            {/* Live Testing with Digital Meters */}
            {testing && (
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-12 border-4 border-cyan-400 shadow-2xl">
                <h3 className="text-3xl font-bold text-white mb-2">Testing Your Connection...</h3>
                <p className="text-cyan-300 mb-8 text-lg">
                  {testPhase === 'testing' && '⚡ Initiating speed test...'}
                  {testPhase === 'ping' && '📡 Measuring latency...'}
                  {testPhase === 'download' && '⬇️ Testing download speed...'}
                  {testPhase === 'upload' && '⬆️ Testing upload speed...'}
                </p>
                
                {/* Three Digital Meters */}
                <div className="grid md:grid-cols-3 gap-8">
                  <DigitalMeter 
                    value={currentDownload} 
                    label="Download"
                    color="cyan"
                    unit="Mbps"
                    icon={Download}
                  />
                  <DigitalMeter 
                    value={currentUpload} 
                    label="Upload"
                    color="purple"
                    unit="Mbps"
                    icon={Upload}
                  />
                  <DigitalMeter 
                    value={currentPing} 
                    label="Ping"
                    color="blue"
                    unit="ms"
                    icon={Activity}
                  />
                </div>

                <p className="text-cyan-200 mt-8">Real-time measurements from backend server</p>
              </div>
            )}

            {/* Results Dashboard */}
            {testComplete && (
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border-4 border-cyan-400 shadow-2xl">
                <h3 className="text-3xl font-bold text-white mb-8">Your Speed Test Results</h3>
                
                {/* Three Digital Meters */}
                <div className="grid md:grid-cols-3 gap-8 mb-8">
                  <DigitalMeter 
                    value={results.download} 
                    label="Download"
                    color="cyan"
                    unit="Mbps"
                    icon={Download}
                  />
                  <DigitalMeter 
                    value={results.upload} 
                    label="Upload"
                    color="purple"
                    unit="Mbps"
                    icon={Upload}
                  />
                  <DigitalMeter 
                    value={results.ping} 
                    label="Ping"
                    color="blue"
                    unit="ms"
                    icon={Activity}
                  />
                </div>

                {/* Verdict */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 border-2 border-cyan-500 mb-6">
                  <p className={`text-xl font-bold ${getSpeedVerdict(results.download).color}`}>
                    {getSpeedVerdict(results.download).text}
                  </p>
                  {results.server && (
                    <p className="text-cyan-300 text-sm mt-2">
                      Server: {results.server}
                    </p>
                  )}
                </div>

                <Button
                  onClick={startSpeedTest}
                  className="h-14 px-8 text-lg font-bold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl"
                >
                  Test Again
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Test With Us Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-center text-white mb-4">
            Why Choose Our Speed Test?
          </h2>
          <p className="text-center text-cyan-200 text-xl mb-16">Advanced testing for modern internet needs</p>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-gradient-to-br from-slate-800 to-slate-900 border-4 border-cyan-500 hover:border-blue-500 transition-all duration-300 shadow-2xl hover:shadow-cyan-500/50">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <div className="text-white">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{feature.title}</h3>
                  <p className="text-cyan-200 text-lg leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tips & Insights Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-900">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-center text-white mb-6">
            What Affects Your Internet Speed?
          </h2>
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-10 border-4 border-cyan-400 shadow-2xl">
            <p className="text-xl text-cyan-100 leading-relaxed mb-8">
              Router distance, network congestion, and background apps can all affect performance. 
              Your Wi-Fi signal strength, the number of connected devices, and even the time of day 
              can impact your internet speed. Understanding these factors helps you optimize your connection.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-bold text-lg mb-2">Router Placement</h4>
                  <p className="text-cyan-200">Keep your router in a central, elevated location</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-bold text-lg mb-2">Device Management</h4>
                  <p className="text-cyan-200">Limit simultaneous connections during heavy usage</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-bold text-lg mb-2">Use Ethernet</h4>
                  <p className="text-cyan-200">Wired connections are faster and more stable</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-bold text-lg mb-2">Close Background Apps</h4>
                  <p className="text-cyan-200">Stop downloads and updates during testing</p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => navigate('/about')}
              className="h-14 px-8 text-lg font-bold bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-xl"
            >
              📘 Learn How to Boost Your Speed
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 via-blue-600 to-violet-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Not happy with your speed?
          </h2>
          <p className="text-2xl text-white mb-10">
            Switch to a connection built for performance.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button
              onClick={() => navigate('/signup')}
              className="h-16 px-10 text-xl font-bold bg-white text-blue-600 hover:bg-gray-100 shadow-2xl hover:scale-105 transition-transform"
            >
              🔘 Explore Our Plans
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
            <Button
              onClick={() => navigate('/about')}
              variant="outline"
              className="h-16 px-10 text-xl font-bold border-4 border-white text-white hover:bg-white/20 shadow-2xl"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SpeedTest;
